import type { ReactNode } from 'react';
import { formatCalendarDate } from '../../lib/format';
import type { Id } from '../../api/types';
import { cadenceLabel } from './occurrence-model';
import type { Recurrence } from './recurrence-api';

/**
 * The recurring-work TEMPLATES for one project.
 *
 * ⭐ Each row carries its own missed count, because a template's health is the only reason to look at
 * this list. A list of titles and cadences answers "what duties exist"; nobody asks that on its own —
 * they ask "which of them has stopped happening".
 *
 * 🔴 `missed_count` AND `occurrence_count` ARE THE SERVER'S. They are computed by a LATERAL over the
 * whole template's history. ⛔ Not recomputed from the occurrences this screen happened to expand:
 * that number would be smaller, would look just as authoritative, and would under-report exactly
 * when it mattered.
 *
 * ⛔ THERE IS NO DELETE, only Pause — and the API enforces it rather than trusting this screen:
 * `DELETE /recurrences/{id}` refuses once the template has occurrences, because the cascade would
 * erase the record of every period it was responsible for, including the missed ones.
 */
export function TemplateList({
  templates,
  openTemplate,
  busyId,
  errors,
  onToggleOpen,
  onTogglePaused,
  renderOccurrences,
}: {
  templates: readonly Recurrence[];
  openTemplate: Id | null;
  busyId: Id | null;
  errors: Record<string, string>;
  onToggleOpen: (template: Recurrence) => void;
  onTogglePaused: (template: Recurrence) => void;
  renderOccurrences: (template: Recurrence) => ReactNode;
}) {
  return (
    <ul className="space-y-2 px-3 pb-4 sm:px-4">
      {templates.map((template) => {
        const expanded = String(openTemplate) === String(template.id);
        return (
          <li
            key={template.id}
            aria-label={template.title}
            className={`rounded border px-3 py-3 ${
              template.missed_count > 0
                ? 'border-red-400 bg-red-50/60 dark:border-red-700 dark:bg-red-950/30'
                : 'border-[color:var(--color-line)] bg-[color:var(--color-surface-raised)]'
            }`}
          >
            <div className="flex flex-wrap items-start gap-2">
              <span className="min-w-0 flex-1 text-sm font-semibold">{template.title}</span>
              {!template.active ? (
                <span className="rounded border border-dashed border-[color:var(--color-line)] px-1.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase">
                  Paused
                </span>
              ) : null}
              <button
                type="button"
                role="switch"
                aria-checked={template.active}
                disabled={busyId === template.id}
                title={template.active ? 'Pause generation' : 'Resume'}
                onClick={() => onTogglePaused(template)}
                className={`h-8 rounded-full px-3 text-xs font-semibold disabled:opacity-50 ${
                  template.active
                    ? 'bg-[color:var(--color-ink)] text-[color:var(--color-surface)]'
                    : 'border border-dashed border-[color:var(--color-line)] text-[color:var(--color-ink-muted)]'
                }`}
              >
                {template.active ? 'Active' : 'Paused'}
              </button>
            </div>

            {template.description ? (
              <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
                {template.description}
              </p>
            ) : null}

            <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[color:var(--color-ink-muted)]">
              <div>
                <dt className="inline font-semibold">Cadence: </dt>
                <dd className="inline">{cadenceLabel(template.cadence, template.interval_n)}</dd>
              </div>
              <div>
                <dt className="inline font-semibold">Owner: </dt>
                <dd className="inline">{template.assignee ?? 'unassigned'}</dd>
              </div>
              <div>
                <dt className="inline font-semibold">From: </dt>
                <dd className="inline">
                  {formatCalendarDate(template.starts_on)}
                  {/* ⚠️ NULL means unbounded by design — "runs until paused", not "we do not know". */}
                  {template.ends_on ? ` to ${formatCalendarDate(template.ends_on)}` : ' — until paused'}
                </dd>
              </div>
            </dl>

            <div className="mt-2 flex flex-wrap items-center gap-3">
              {template.missed_count > 0 ? (
                <p className="text-xs font-bold text-red-800 dark:text-red-200">
                  <span aria-hidden="true">⛔ </span>
                  {template.missed_count} missed of {template.occurrence_count} expected
                </p>
              ) : template.occurrence_count > 0 ? (
                <p className="text-xs text-[color:var(--color-ink-muted)]">
                  {template.occurrence_count} expected, none missed
                </p>
              ) : (
                /* ⚠️ Distinct from "none missed". No periods generated is not a clean bill of health —
                   there is nothing yet that COULD have been missed. */
                <p className="text-xs text-[color:var(--color-ink-muted)]">
                  No periods generated yet — nothing has been expected of this duty.
                </p>
              )}

              <button
                type="button"
                aria-expanded={expanded}
                onClick={() => onToggleOpen(template)}
                className="h-8 rounded border border-[color:var(--color-line)] px-3 text-xs font-semibold"
              >
                {expanded ? 'Hide periods' : 'Show every period'}
              </button>
            </div>

            {errors[String(template.id)] ? (
              <p role="alert" className="mt-2 text-xs font-semibold text-red-700 dark:text-red-300">
                {errors[String(template.id)]}
              </p>
            ) : null}

            {renderOccurrences(template)}
          </li>
        );
      })}
    </ul>
  );
}
